import type { UserRideSummary } from "../data/rideService";

export const ACTIVITY_PROFILES = ["bicycle", "electric_bike", "hiking", "motor_vehicle"] as const;
export type ActivityProfileKey = (typeof ACTIVITY_PROFILES)[number];

export const PROFILE_CORRECTION_REASONS = {
  confirmedAsIs: "confirmed_as_is",
  excludedFromRecords: "excluded_from_records",
  includedInRecords: "included_in_records",
  profileChanged: "profile_changed"
} as const;

export const ACTIVITY_SUSPICIOUS_FLAGS = {
  bicycleSpeedHigh: "bicycle_speed_high",
  carLikeSpeed: "car_like_speed",
  ebikeSpeedHigh: "electric_bike_speed_high",
  hikingSpeedHigh: "hiking_speed_high",
  teleportJump: "teleport_jump"
} as const;

const profileSet = new Set<string>(ACTIVITY_PROFILES);
const recordProfiles = new Set<string>(ACTIVITY_PROFILES);
const segmentProfiles = new Set<string>(["bicycle", "electric_bike", "hiking"]);
const teleportSegmentSpeedKmh = 160;
const teleportMaxSeconds = 20;

export function normalizeActivityProfile(profileKey?: string | null): ActivityProfileKey | undefined {
  const normalized = profileKey?.trim().toLowerCase();
  return normalized && profileSet.has(normalized) ? normalized as ActivityProfileKey : undefined;
}

export function normalizeRecordProfile(profileKey?: string | null): ActivityProfileKey | undefined {
  const normalized = normalizeActivityProfile(profileKey);
  return normalized && recordProfiles.has(normalized) ? normalized : undefined;
}

export function normalizeSegmentProfile(profileKey?: string | null): ActivityProfileKey | undefined {
  const normalized = normalizeActivityProfile(profileKey);
  return normalized && segmentProfiles.has(normalized) ? normalized : undefined;
}

export function detectSuspiciousActivityFlags(ride: UserRideSummary) {
  const profileKey = normalizeActivityProfile(ride.recordingProfileKey);
  if (!profileKey || profileKey === "motor_vehicle") return [];

  const pointMaxSpeedKmh = ride.routePoints.reduce((max, point) => {
    const speedKmh = Number.isFinite(point.speed) ? point.speed * 3.6 : 0;
    return Math.max(max, speedKmh);
  }, 0);
  const effectiveMaxSpeed = Math.max(ride.maxSpeedKmh || 0, pointMaxSpeedKmh);
  const effectiveAverageSpeed = ride.averageSpeedKmh || 0;
  const flags = new Set<string>();

  if (profileKey === "hiking" && (effectiveMaxSpeed > 24 || effectiveAverageSpeed > 11)) {
    flags.add(ACTIVITY_SUSPICIOUS_FLAGS.hikingSpeedHigh);
  }
  if (profileKey === "bicycle" && (effectiveMaxSpeed > 90 || effectiveAverageSpeed > 55)) {
    flags.add(ACTIVITY_SUSPICIOUS_FLAGS.bicycleSpeedHigh);
  }
  if (profileKey === "electric_bike" && (effectiveMaxSpeed > 110 || effectiveAverageSpeed > 65)) {
    flags.add(ACTIVITY_SUSPICIOUS_FLAGS.ebikeSpeedHigh);
  }
  if (
    effectiveAverageSpeed > 70 ||
    ride.routePoints.filter((point) => Number.isFinite(point.speed) && point.speed * 3.6 > 95).length >= 3
  ) {
    flags.add(ACTIVITY_SUSPICIOUS_FLAGS.carLikeSpeed);
  }
  if (hasTeleportJump(ride)) {
    flags.add(ACTIVITY_SUSPICIOUS_FLAGS.teleportJump);
  }

  return [...flags];
}

export function suspiciousFlagsForRide(ride: UserRideSummary) {
  return ride.suspiciousFlags.length > 0 ? ride.suspiciousFlags : detectSuspiciousActivityFlags(ride);
}

export function hasConfirmedActivityDecision(ride: UserRideSummary) {
  return (
    ride.profileCorrectionReason === PROFILE_CORRECTION_REASONS.confirmedAsIs ||
    ride.profileCorrectionReason === PROFILE_CORRECTION_REASONS.profileChanged
  );
}

export function hasUnconfirmedSuspiciousActivity(ride: UserRideSummary) {
  return suspiciousFlagsForRide(ride).length > 0 && !hasConfirmedActivityDecision(ride);
}

export function isEligibleForPerformanceStats(ride: UserRideSummary) {
  return !ride.isDeleted && !ride.isFromOtherRider && !ride.excludedFromStats && !hasUnconfirmedSuspiciousActivity(ride);
}

export function isEligibleForPersonalRecords(ride: UserRideSummary) {
  return (
    !ride.isDeleted &&
    !ride.isFromOtherRider &&
    !ride.excludedFromRecords &&
    !hasUnconfirmedSuspiciousActivity(ride) &&
    normalizeRecordProfile(ride.recordingProfileKey) !== undefined
  );
}

export function isEligibleForSegmentOrClimbWork(ride: UserRideSummary) {
  return (
    !ride.isDeleted &&
    !ride.isFromOtherRider &&
    !ride.excludedFromRecords &&
    !hasUnconfirmedSuspiciousActivity(ride) &&
    normalizeSegmentProfile(ride.recordingProfileKey) !== undefined
  );
}

function hasTeleportJump(ride: UserRideSummary) {
  return ride.routePoints.some((point, index) => {
    if (index === 0) return false;
    const previous = ride.routePoints[index - 1];
    const seconds = (point.timestamp - previous.timestamp) / 1000;
    if (seconds <= 0 || seconds > teleportMaxSeconds) return false;
    const speedKmh = (distanceMeters(previous.latitude, previous.longitude, point.latitude, point.longitude) / seconds) * 3.6;
    return Number.isFinite(speedKmh) && speedKmh > teleportSegmentSpeedKmh;
  });
}

function distanceMeters(fromLat: number, fromLng: number, toLat: number, toLng: number) {
  const earthRadiusMeters = 6371000;
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(toLat - fromLat);
  const dLng = toRad(toLng - fromLng);
  const lat1 = toRad(fromLat);
  const lat2 = toRad(toLat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(Math.min(Math.max(h, 0), 1)), Math.sqrt(1 - Math.min(Math.max(h, 0), 1)));
}
